import { useState, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { subDays, startOfMonth, startOfDay, endOfDay, format, differenceInMinutes } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { supabase } from '@/integrations/supabase/client'
import { useOrganizationContext } from '@/contexts/OrganizationContext'
import { useUserPermissions } from '@/hooks/useUserPermissions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  Users, Clock, MessageSquare, Timer, LogIn, LogOut,
  Activity, RefreshCw, XCircle, UserCheck, Lock, Star,
} from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────────────────────

type Period = 'today' | '7days' | 'month'
type OpStatus = 'online' | 'ausente' | 'pausa' | 'offline'

interface PerfEvent {
  id: string
  event_type: string
  user_id: string
  metadata: Record<string, any>
  created_at: string
  user_name: string | null
  user_email: string | null
  avatar_url: string | null
}

interface CurrentStatus {
  user_id: string
  status: string
  updated_at: string
  user_name: string | null
  user_email: string | null
  avatar_url: string | null
}

interface OperatorRow {
  id: string
  name: string | null
  email: string | null
  avatar_url: string | null
  status: OpStatus
  onlineMs: number
  chats: number
  tmaSeconds: number
  lastEventAt: string | null
}

interface TimelineRow {
  id: string
  name: string | null
  email: string | null
  avatar_url: string | null
  segments: { leftPct: number; widthPct: number; color: string; status: string }[]
}

interface CsatData {
  operator_id: string
  rated_chats: number
  avg_nota: number
  satisfaction_pct: number
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CFG: Record<OpStatus, { label: string; color: string; pill: string }> = {
  online:  { label: 'Online',  color: '#16a34a', pill: 'bg-green-100 text-green-700' },
  ausente: { label: 'Ausente', color: '#d97706', pill: 'bg-amber-100 text-amber-700' },
  pausa:   { label: 'Pausa',   color: '#ea580c', pill: 'bg-orange-100 text-orange-700' },
  offline: { label: 'Offline', color: '#94a3b8', pill: 'bg-slate-100 text-slate-500' },
}

const EVENT_CFG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
  user_login:              { label: 'Login',           color: '#16a34a', Icon: LogIn        },
  user_logout:             { label: 'Logout',          color: '#94a3b8', Icon: LogOut       },
  operator_status_changed: { label: 'Status alterado', color: '#d97706', Icon: Activity     },
  chat_assigned:           { label: 'Chat atribuído',  color: '#7c3aed', Icon: UserCheck    },
  chat_closed:             { label: 'Chat encerrado',  color: '#0891b2', Icon: XCircle      },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function displayName(name: string | null, email: string | null) {
  return name || email || 'Operador'
}

function initials(name: string | null, email: string | null) {
  const src = name || email || '?'
  const parts = src.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return src.substring(0, 2).toUpperCase()
}

function fmtDuration(ms: number) {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function computeOnlineMs(events: PerfEvent[], userId: string): number {
  const sorted = events
    .filter(e => e.user_id === userId && e.event_type === 'operator_status_changed')
    .sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at))

  let total = 0
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].metadata?.to === 'online') {
      const start = +new Date(sorted[i].created_at)
      const end = sorted[i + 1] ? +new Date(sorted[i + 1].created_at) : Date.now()
      total += Math.max(0, end - start)
    }
  }
  return total
}

function computeTMA(events: PerfEvent[], userId: string | 'all'): number {
  const assigned = events.filter(e => {
    if (e.event_type !== 'chat_assigned') return false
    const to = e.metadata?.assigned_to_id ?? e.user_id
    return userId === 'all' || to === userId
  })
  if (!assigned.length) return 0

  let total = 0, count = 0
  for (const a of assigned) {
    const chatId = a.metadata?.chat_id
    if (!chatId) continue
    const closed = events.find(e => e.event_type === 'chat_closed' && e.metadata?.chat_id === chatId)
    if (closed) {
      const diff = (+new Date(closed.created_at) - +new Date(a.created_at)) / 1000
      if (diff > 0) { total += diff; count++ }
    }
  }
  return count ? total / count : 0
}

function buildTimelineSegments(
  events: PerfEvent[],
  userId: string,
  dayStart: number,
  totalMs: number,
): TimelineRow['segments'] {
  const sorted = events
    .filter(e => e.user_id === userId && e.event_type === 'operator_status_changed')
    .sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at))

  const segments: TimelineRow['segments'] = []
  for (let i = 0; i < sorted.length; i++) {
    const toStatus = sorted[i].metadata?.to as OpStatus
    if (!toStatus || toStatus === 'offline') continue
    const start = +new Date(sorted[i].created_at)
    const end = sorted[i + 1] ? +new Date(sorted[i + 1].created_at) : Math.min(Date.now(), dayStart + 86_400_000)
    const leftPct = ((start - dayStart) / totalMs) * 100
    const widthPct = ((end - start) / totalMs) * 100
    if (widthPct > 0.05 && leftPct < 100) {
      segments.push({
        leftPct: Math.max(0, Math.min(leftPct, 100)),
        widthPct: Math.min(widthPct, 100 - Math.max(0, leftPct)),
        color: STATUS_CFG[toStatus]?.color ?? '#94a3b8',
        status: toStatus,
      })
    }
  }
  return segments
}

function eventDescription(e: PerfEvent): string {
  switch (e.event_type) {
    case 'user_login':  return 'fez login'
    case 'user_logout': return 'fez logout'
    case 'operator_status_changed': {
      const from = e.metadata?.from as OpStatus
      const to   = e.metadata?.to   as OpStatus
      return `alterou status de ${STATUS_CFG[from]?.label ?? from} para ${STATUS_CFG[to]?.label ?? to}`
    }
    case 'chat_assigned': {
      const name = e.metadata?.assigned_to_name
      return name ? `atribuiu chat para ${name}` : 'atribuiu chat'
    }
    case 'chat_closed': {
      const reason = e.metadata?.reason
      return reason ? `encerrou chat (${reason})` : 'encerrou chat'
    }
    default: return e.event_type
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  icon: Icon, label, value, color,
}: { icon: React.ElementType; label: string; value: string | number; color: string }) {
  return (
    <Card className="rounded-3xl border-0 shadow-sm bg-white">
      <CardContent className="p-5 flex flex-col gap-4">
        <div className="rounded-xl p-2 w-fit" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div>
          <p className="text-[30px] font-bold text-[#0f172a] leading-none tracking-tight">{value}</p>
          <p className="text-[12px] text-[#94a3b8] font-medium mt-2 leading-tight">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: OpStatus }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.offline
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold', cfg.pill)}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cfg.color }} />
      {cfg.label}
    </span>
  )
}

function OperatorAvatar({
  name, email, avatarUrl, size = 8,
}: { name: string | null; email: string | null; avatarUrl?: string | null; size?: number }) {
  const dim = `h-${size} w-${size}`
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName(name, email)}
        className={`${dim} rounded-full object-cover shrink-0 ring-1 ring-[#e2e8f0]`}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; (e.currentTarget.nextSibling as HTMLElement)?.removeAttribute('hidden') }}
      />
    )
  }
  return (
    <div className={`${dim} rounded-full bg-[#eef1f4] flex items-center justify-center shrink-0`}>
      <span className="text-[11px] font-bold text-[#4a5565]">{initials(name, email)}</span>
    </div>
  )
}

function CsatBadge({ csat }: { csat: CsatData | undefined }) {
  if (!csat || Number(csat.rated_chats) === 0) return <span className="text-[13px] text-[#94a3b8]">—</span>
  const nota = Number(csat.avg_nota)
  const color = nota >= 8 ? '#16a34a' : nota >= 6 ? '#d97706' : '#dc2626'
  return (
    <div className="flex items-center gap-1.5">
      <Star className="h-3.5 w-3.5" style={{ color, fill: color }} />
      <span className="text-[13px] font-semibold" style={{ color }}>{nota.toFixed(1)}</span>
      <span className="text-[11px] text-[#94a3b8]">({csat.rated_chats})</span>
    </div>
  )
}

function OperatorTable({ rows, csatMap }: { rows: OperatorRow[]; csatMap: Map<string, CsatData> }) {
  if (!rows.length) return (
    <Card className="border-0 shadow-sm bg-white rounded-2xl">
      <CardContent className="p-10 text-center text-sm text-[#94a3b8]">Nenhum operador com eventos no período.</CardContent>
    </Card>
  )
  return (
    <Card className="border-0 shadow-sm bg-white rounded-2xl h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-[15px] font-semibold text-[#0f172a]">Operadores</CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-hidden">
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow className="border-[#f1f5f9] hover:bg-transparent">
              <TableHead className="text-[12px] text-[#94a3b8] font-medium pl-4 w-[28%]">Operador</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[20%]">Status</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[16%]">T. online</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[10%]">Chats</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[10%]">TMA</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[10%]">CSAT</TableHead>
              <TableHead className="text-[12px] text-[#94a3b8] font-medium w-[16%]">Último ev.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} className="border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">
                <TableCell className="pl-4 py-3">
                  <div className="flex items-center gap-2">
                    <OperatorAvatar name={row.name} email={row.email} avatarUrl={row.avatar_url} />
                    <p className="text-[13px] font-medium text-[#0f172a] truncate">{displayName(row.name, row.email)}</p>
                  </div>
                </TableCell>
                <TableCell><StatusBadge status={row.status} /></TableCell>
                <TableCell className="text-[13px] text-[#4a5565]">
                  {row.onlineMs > 0 ? fmtDuration(row.onlineMs) : '—'}
                </TableCell>
                <TableCell className="text-[13px] font-semibold text-[#0f172a]">{row.chats}</TableCell>
                <TableCell className="text-[13px] text-[#4a5565]">
                  {row.tmaSeconds > 0 ? fmtDuration(row.tmaSeconds * 1000) : '—'}
                </TableCell>
                <TableCell>
                  <CsatBadge csat={csatMap.get(row.id)} />
                </TableCell>
                <TableCell className="text-[12px] text-[#94a3b8]">
                  {row.lastEventAt
                    ? format(new Date(row.lastEventAt), 'HH:mm', { locale: ptBR })
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// 49 marks: 00:00, 00:30, 01:00 … 24:00
const TIMELINE_MARKS = Array.from({ length: 49 }, (_, i) => ({
  pct: (i / 48) * 100,
  isHour: i % 2 === 0,
  hour: Math.floor(i / 2),
}))

function StatusTimeline({ rows, dayStart }: { rows: TimelineRow[]; dayStart: Date }) {
  if (!rows.length) return null

  return (
    <>
    <style>{`
      @keyframes shimmer-slide {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `}</style>
    <Card className="border-0 shadow-sm bg-white rounded-2xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px] font-semibold text-[#0f172a]">Linha do Tempo — Hoje</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Axis: hour labels + half-hour ticks */}
        <div className="flex pl-[156px] mb-2">
          <div className="flex-1 relative h-5">
            {TIMELINE_MARKS.map(({ pct, isHour, hour }) => (
              <div
                key={pct}
                className="absolute bottom-0 flex flex-col items-center"
                style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
              >
                {isHour && (
                  <span className="text-[9px] text-[#94a3b8] select-none whitespace-nowrap leading-tight">
                    {String(hour).padStart(2, '0')}h
                  </span>
                )}
                <div className={`w-px ${isHour ? 'h-2.5 bg-[#cbd5e1]' : 'h-1.5 bg-[#dde3ea]'}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Operator rows */}
        <div className="space-y-1.5">
          {rows.map(row => (
            <div key={row.id} className="flex items-center gap-3">
              <div className="w-36 flex items-center gap-2 shrink-0" title={displayName(row.name, row.email)}>
                <OperatorAvatar name={row.name} email={row.email} avatarUrl={row.avatar_url} size={6} />
                <span className="text-[12px] text-[#64748b] truncate flex-1">{displayName(row.name, row.email)}</span>
              </div>
              <div className="flex-1 relative h-3 bg-[#f1f5f9] rounded-full overflow-hidden">
                {row.segments.map((seg, i) => (
                  <div
                    key={i}
                    className="absolute top-0 h-full overflow-hidden"
                    style={{
                      left: `${seg.leftPct}%`,
                      width: `${seg.widthPct}%`,
                      backgroundColor: seg.color,
                      opacity: 0.85,
                    }}
                  >
                    {seg.status === 'online' && (
                      <div
                        className="absolute inset-0"
                        style={{
                          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                          animation: 'shimmer-slide 3.5s ease-in-out infinite',
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mt-4 flex-wrap">
          {(Object.entries(STATUS_CFG) as [OpStatus, typeof STATUS_CFG[OpStatus]][]).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: cfg.color }} />
              <span className="text-[11px] text-[#64748b]">{cfg.label}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    </>
  )
}

function EventLog({ events }: { events: PerfEvent[] }) {
  if (!events.length) return (
    <Card className="border-0 shadow-sm bg-white rounded-2xl">
      <CardContent className="p-10 text-center text-sm text-[#94a3b8]">Nenhum evento registrado no período.</CardContent>
    </Card>
  )
  return (
    <Card className="border-0 shadow-sm bg-white rounded-2xl h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-[15px] font-semibold text-[#0f172a]">Log de Eventos</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[320px]">
          <div className="divide-y divide-[#f1f5f9]">
            {events.map(e => {
              const cfg = EVENT_CFG[e.event_type] ?? { label: e.event_type, color: '#94a3b8', Icon: Activity }
              const { Icon } = cfg
              return (
                <div key={e.id} className="flex items-start gap-3 px-6 py-3 hover:bg-[#f8fafc] transition-colors">
                  <OperatorAvatar name={e.user_name} email={e.user_email} avatarUrl={e.avatar_url} size={7} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-[#0f172a]">
                      <span className="font-medium">{displayName(e.user_name, e.user_email)}</span>
                      {' '}
                      <span className="text-[#64748b]">{eventDescription(e)}</span>
                    </p>
                  </div>
                  <time className="text-[11px] text-[#94a3b8] shrink-0 mt-0.5">
                    {format(new Date(e.created_at), 'HH:mm:ss', { locale: ptBR })}
                  </time>
                </div>
              )
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Performance() {
  const { currentOrg } = useOrganizationContext()
  const { isAdmin, isLoading: permLoading } = useUserPermissions()
  const queryClient = useQueryClient()

  const [period, setPeriod]           = useState<Period>('today')
  const [selectedOp, setSelectedOp]   = useState<string>('all')
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [liveStatus, setLiveStatus]   = useState<'connecting' | 'connected' | 'error'>('connecting')

  // Date range derived from period
  const dateRange = useMemo(() => {
    const now = new Date()
    switch (period) {
      case 'today':  return { from: startOfDay(now).toISOString(), to: endOfDay(now).toISOString() }
      case '7days':  return { from: subDays(startOfDay(now), 6).toISOString(), to: endOfDay(now).toISOString() }
      case 'month':  return { from: startOfMonth(now).toISOString(), to: endOfDay(now).toISOString() }
    }
  }, [period])

  // Fetch all events for the period
  const { data: events = [], isFetching } = useQuery<PerfEvent[]>({
    queryKey: ['perf-events', currentOrg?.id, dateRange],
    queryFn: async () => {
      if (!currentOrg?.id) return []
      const { data, error } = await supabase.rpc('get_performance_events', {
        p_org_id: currentOrg.id,
        p_from:   dateRange.from,
        p_to:     dateRange.to,
      })
      if (error) throw error
      return (data ?? []) as PerfEvent[]
    },
    enabled: !!currentOrg?.id && isAdmin,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  // Fetch current status for all operators (all-time, not period-scoped)
  const { data: currentStatuses = [] } = useQuery<CurrentStatus[]>({
    queryKey: ['perf-status', currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) return []
      const { data, error } = await supabase.rpc('get_current_operator_status', {
        p_org_id: currentOrg.id,
      })
      if (error) throw error
      return (data ?? []) as CurrentStatus[]
    },
    enabled: !!currentOrg?.id && isAdmin,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  // Fetch CSAT data for the period
  const { data: csatList = [] } = useQuery<CsatData[]>({
    queryKey: ['perf-csat', currentOrg?.id, dateRange],
    queryFn: async () => {
      if (!currentOrg?.id) return []
      const { data, error } = await supabase.rpc('get_csat_data', {
        p_org_id: currentOrg.id,
        p_from:   dateRange.from,
        p_to:     dateRange.to,
      })
      if (error) throw error
      return (data ?? []) as CsatData[]
    },
    enabled: !!currentOrg?.id && isAdmin,
    refetchInterval: 120_000,
    staleTime: 30_000,
  })

  const csatMap = useMemo(() =>
    new Map(csatList.map(c => [c.operator_id, c])),
    [csatList]
  )

  const handleRefresh = () => {
    if (!currentOrg?.id) return
    queryClient.invalidateQueries({ queryKey: ['perf-events', currentOrg.id] })
    queryClient.invalidateQueries({ queryKey: ['perf-status', currentOrg.id] })
    queryClient.invalidateQueries({ queryKey: ['perf-csat',   currentOrg.id] })
    setLastUpdated(new Date())
  }

  // Update lastUpdated when events refresh
  useEffect(() => {
    if (!isFetching) setLastUpdated(new Date())
  }, [isFetching])

  // Realtime subscription — events.user_events
  useEffect(() => {
    if (!currentOrg?.id || !isAdmin) return

    const orgId = currentOrg.id
    const channel = supabase
      .channel(`perf-live-${orgId}`)
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'events', table: 'user_events' },
        (payload: any) => {
          if (String(payload.new?.org_id) !== String(orgId)) return
          queryClient.invalidateQueries({ queryKey: ['perf-events', orgId] })
          if (payload.new?.event_type === 'operator_status_changed') {
            queryClient.invalidateQueries({ queryKey: ['perf-status', orgId] })
          }
          setLastUpdated(new Date())
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setLiveStatus('connected')
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setLiveStatus('error')
      })

    return () => {
      supabase.removeChannel(channel)
      setLiveStatus('connecting')
    }
  }, [currentOrg?.id, isAdmin, queryClient])

  // All known operators (union of status + events)
  const allOperators = useMemo(() => {
    const map = new Map<string, { name: string | null; email: string | null; avatar_url: string | null }>()
    currentStatuses.forEach(s => map.set(s.user_id, { name: s.user_name, email: s.user_email, avatar_url: s.avatar_url }))
    events.forEach(e => {
      if (e.user_id && !map.has(e.user_id))
        map.set(e.user_id, { name: e.user_name, email: e.user_email, avatar_url: e.avatar_url })
    })
    return Array.from(map.entries()).map(([id, info]) => ({ id, ...info }))
  }, [currentStatuses, events])

  // KPIs
  const kpis = useMemo(() => {
    const onlineNow = currentStatuses.filter(s => s.status === 'online').length

    const chatsToday = events.filter(e => {
      if (e.event_type !== 'chat_assigned') return false
      const to = e.metadata?.assigned_to_id ?? e.user_id
      return selectedOp === 'all' || to === selectedOp
    }).length

    const loginsToday = events.filter(e =>
      e.event_type === 'user_login' && (selectedOp === 'all' || e.user_id === selectedOp)
    ).length

    const opIds = selectedOp === 'all' ? allOperators.map(o => o.id) : [selectedOp]
    const onlineMsArr = opIds.map(id => computeOnlineMs(events, id))
    const avgOnlineMs = onlineMsArr.length ? onlineMsArr.reduce((a, b) => a + b, 0) / onlineMsArr.length : 0

    const tmaSeconds = computeTMA(events, selectedOp)

    // CSAT médio ponderado pelo número de avaliações
    const csatFiltered = selectedOp === 'all'
      ? csatList
      : csatList.filter(c => c.operator_id === selectedOp)
    const totalRated = csatFiltered.reduce((s, c) => s + Number(c.rated_chats), 0)
    const avgCsat = totalRated > 0
      ? csatFiltered.reduce((s, c) => s + Number(c.avg_nota) * Number(c.rated_chats), 0) / totalRated
      : null

    return { onlineNow, chatsToday, loginsToday, avgOnlineMs, tmaSeconds, avgCsat, totalRated }
  }, [currentStatuses, events, allOperators, selectedOp, csatList])

  // Operator table rows
  const operatorRows = useMemo<OperatorRow[]>(() => {
    const statusOrder: Record<OpStatus, number> = { online: 0, ausente: 1, pausa: 2, offline: 3 }
    return allOperators
      .filter(op => selectedOp === 'all' || op.id === selectedOp)
      .map(op => {
        const cur = currentStatuses.find(s => s.user_id === op.id)
        const status = (cur?.status ?? 'offline') as OpStatus
        const onlineMs = computeOnlineMs(events, op.id)
        const chats = events.filter(e =>
          e.event_type === 'chat_assigned' &&
          (e.metadata?.assigned_to_id === op.id || (!e.metadata?.assigned_to_id && e.user_id === op.id))
        ).length
        const tmaSeconds = computeTMA(events, op.id)
        const lastEvent = events
          .filter(e => e.user_id === op.id)
          .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))[0]
        const avatar_url = currentStatuses.find(s => s.user_id === op.id)?.avatar_url ?? op.avatar_url ?? null
        return { id: op.id, name: op.name, email: op.email, avatar_url, status, onlineMs, chats, tmaSeconds, lastEventAt: lastEvent?.created_at ?? null }
      })
      .sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3))
  }, [allOperators, currentStatuses, events, selectedOp])

  // Timeline rows (today only)
  const dayStart = useMemo(() => startOfDay(new Date()), [])
  const timelineRows = useMemo<TimelineRow[]>(() => {
    if (period !== 'today') return []
    const totalMs = 86_400_000
    return allOperators
      .filter(op => selectedOp === 'all' || op.id === selectedOp)
      .map(op => ({
        id: op.id,
        name: op.name,
        email: op.email,
        avatar_url: op.avatar_url ?? null,
        segments: buildTimelineSegments(events, op.id, dayStart.getTime(), totalMs),
      }))
      .filter(r => r.segments.length > 0)
  }, [allOperators, events, selectedOp, period, dayStart])

  // Event log
  const recentEvents = useMemo(() =>
    events
      .filter(e => selectedOp === 'all' || e.user_id === selectedOp || e.metadata?.assigned_to_id === selectedOp)
      .slice(0, 100),
    [events, selectedOp]
  )

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (permLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]" />
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-[#64748b]">
        <Lock className="h-10 w-10 text-[#e2e8f0]" />
        <p className="text-sm font-medium">Acesso restrito a administradores.</p>
      </div>
    )
  }

  return (
    <div className="-m-3 -mt-16 sm:-m-4 md:-m-6 md:-mt-6 min-h-screen" style={{ background: 'linear-gradient(to bottom, #8aaece 0%, #f5f5f5 50%, #f5f5f5 100%)' }}>
    <div className="p-3 pt-16 sm:p-4 md:p-6 space-y-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[#0f172a]">Performance</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm border-0 text-[13px] text-black hover:bg-white/30 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Atualizar
          </button>
          <Select value={period} onValueChange={v => setPeriod(v as Period)}>
            <SelectTrigger className="h-9 w-36 text-[13px] bg-white/20 backdrop-blur-sm border-0 text-black">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="7days">Últimos 7 dias</SelectItem>
              <SelectItem value="month">Este mês</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedOp} onValueChange={setSelectedOp}>
            <SelectTrigger className="h-9 w-44 text-[13px] bg-white/20 backdrop-blur-sm border-0 text-black">
              <SelectValue placeholder="Todos os operadores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os operadores</SelectItem>
              {allOperators.map(op => (
                <SelectItem key={op.id} value={op.id}>
                  {displayName(op.name, op.email)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={Users}          label="Online agora"       value={kpis.onlineNow}                                         color="#0033d6" />
        <KpiCard icon={Clock}          label="Tempo médio online"  value={kpis.avgOnlineMs > 0 ? fmtDuration(kpis.avgOnlineMs) : '—'} color="#0033d6" />
        <KpiCard icon={MessageSquare}  label="Chats atendidos"     value={kpis.chatsToday}                                        color="#0033d6" />
        <KpiCard icon={Timer}          label="TMA"                 value={kpis.tmaSeconds > 0 ? fmtDuration(kpis.tmaSeconds * 1000) : '—'} color="#0033d6" />
        <KpiCard icon={LogIn}          label="Logins"              value={kpis.loginsToday}                                       color="#0033d6" />
        <KpiCard icon={Star}           label="CSAT médio"          value={kpis.avgCsat !== null ? `${kpis.avgCsat.toFixed(1)}/10` : '—'}  color="#0033d6" />
      </div>

      {/* Operator Table + Event Log */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <OperatorTable rows={operatorRows} csatMap={csatMap} />
        <EventLog events={recentEvents} />
      </div>

      {/* Timeline (today only) — full width */}
      {period === 'today' && timelineRows.length > 0 && (
        <StatusTimeline rows={timelineRows} dayStart={dayStart} />
      )}

    </div>
    </div>
  )
}
